import { useSessionContext } from '../../../js/app/contexts/SessionContext';
import { WorkflowReason, WorkflowStatus } from '../hooks/useWorkflowMetadata';

export type Audience = 'PROVIDER' | 'HEALTHCARE_ENTITY' | 'STAFF' | 'UNKNOWN';

export function useAudience(): Audience {
  const { audienceIsProvider, audienceIsOrg, audienceIsStaff } = useSessionContext();
  if (audienceIsStaff) return 'STAFF';
  if (audienceIsProvider) return 'PROVIDER';
  if (audienceIsOrg) return 'HEALTHCARE_ENTITY';
  return 'UNKNOWN';
}

type WorkflowItem = WorkflowStatus | WorkflowReason;

export function canUseWorkflowItem(audience: Audience, item: WorkflowItem): boolean {
  switch (audience) {
    case 'STAFF':
      return item.set_by_staff;
    case 'PROVIDER':
      return item.set_by_provider;
    case 'HEALTHCARE_ENTITY':
      return item.set_by_healthcare_entity;
    default:
      return false;
  }
}

export function getRenderableStatuses(
  all: Record<string, WorkflowStatus | undefined>,
  fromStatusCode: string | null | undefined,
  audience: Audience
): WorkflowStatus[] {
  if (!fromStatusCode) return [];
  const current = all[fromStatusCode];
  if (!current) return [];
  const nextCodes = current.allowed_transitions || [];
  return nextCodes
    .map((code) => all[code])
    .filter((s): s is WorkflowStatus => Boolean(s))
    .filter((s) => canUseWorkflowItem(audience, s));
}

export function getRenderableReasons(
  all: Record<string, WorkflowStatus | undefined>,
  statusCode: string | null | undefined,
  audience: Audience
): WorkflowReason[] {
  if (!statusCode) return [];
  const status = all[statusCode];
  if (!status) return [];
  return (status.reasons || []).filter((r) => canUseWorkflowItem(audience, r));
}

export function useWorkflowAccess(statuses: Record<string, WorkflowStatus | undefined>) {
  const audience = useAudience();

  return {
    audience,
    canUse: (item: WorkflowStatus | WorkflowReason) => canUseWorkflowItem(audience, item),
    renderableStatusesFrom: (fromStatusCode?: string | null) =>
      getRenderableStatuses(statuses, fromStatusCode, audience),
    renderableReasonsFor: (statusCode?: string | null) =>
      getRenderableReasons(statuses, statusCode, audience),
  };
}
